import { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useAuthModal, useGuardedNavigate } from "../context/AuthModalContext";
import { useOpenSpace } from "../context/SpaceGateContext";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { getAllCampaigns, getFeaturedCampaigns, getMyCampaigns } from "../api/campaigns";
import { getDiscoverMintSpaces } from "../api/mintSpace";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import { managesMintSpace, productFolderApps } from "../utils/mintSpaceApps";
import { Stagger, StaggerItem } from "../components/motion/Reveal";
import { getBusinessCategoryShortLabel, parseCategory } from "../constants/businessCategories";
import DiscoverCategoryDropdown from "../components/DiscoverCategoryDropdown";
import { getCampaignListPath } from "../utils/campaignCardUtils";
import Button from "../components/ds/Button";
import SearchField from "../components/ds/SearchField";
import SegmentedControl from "../components/ds/SegmentedControl";
import ViewTabs from "../components/ds/ViewTabs";
import FeaturedCampaigns from "../components/discover/FeaturedCampaigns";
import { CampaignTile, ProductTile, Section, SpaceTile } from "../components/discover/DiscoverAll";
import SpacesHero from "../components/discover/SpacesHero";

/*
  Discover, from Figma "ეკრანები · B · პირადი ანგარიში": B1 (campaigns), B1.2 (Mint
  spaces), B1.3 (products), plus the Featured row from "Featured · რჩეული კამპანიები".
  Grids fill whatever width the page has. Campaigns reach four columns and stop:
  each one is at least a quarter of the row, and never under what the card needs.
*/

const CAMPAIGN_GRID = "grid grid-cols-[repeat(auto-fill,minmax(max(200px,calc((100%_-_49px)/4)),1fr))] gap-space-16";
const SPACE_GRID = "grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-space-16 lg:gap-space-24";
const PRODUCT_GRID = "grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-x-space-8 gap-y-space-16";

const EmptyState = ({ title, text }) => (
  <div className="flex flex-col items-center gap-space-4 rounded-radius-xl border border-separator-default bg-bg-raised px-space-24 py-space-32 text-center">
    <p className="text-headline text-label-primary">{title}</p>
    {text && <p className="text-footnote text-label-secondary">{text}</p>}
  </div>
);

/*
  The All view's rows, by Figma's frames: phone 390, tablet 834, desktop 1440, wide
  1920. Each row shows only what fills it, so a wider screen reveals the extra tile.
*/
const ALL_CAMPAIGN_GRID =
  "grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2 lg:gap-space-24 min-[1600px]:grid-cols-3 [&>*:nth-child(3)]:hidden min-[1600px]:[&>*:nth-child(3)]:flex";
const ALL_PRODUCT_GRID =
  "grid grid-cols-2 gap-space-16 min-[600px]:grid-cols-4 lg:grid-cols-3 lg:gap-space-24 min-[1600px]:grid-cols-4 lg:[&>*:nth-child(4)]:hidden min-[1600px]:[&>*:nth-child(4)]:flex";
const ALL_SPACE_GRID =
  "grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2 lg:gap-space-24 min-[1440px]:grid-cols-4 [&>*:nth-child(n+3)]:hidden min-[1440px]:[&>*:nth-child(n+3)]:flex";

const HomePage = ({
  isDark,
  audience,
  setAudience,
  setToast,
  /** When embedded in campaign window, highlight this campaign card in Discover */
  highlightedCampaignId,
}) => {
  const { isAuthenticated, user } = useAuth();
  const { t, language } = useTranslation();
  const navigate = useGuardedNavigate();
  const { openAuthModal } = useAuthModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [titleSlot, setTitleSlot] = useState(null);
  useLayoutEffect(() => setTitleSlot(document.getElementById("shell-title")), []);
  const highlightId = highlightedCampaignId || searchParams.get("campaign") || null;
  /**
   * From URL `?discover=` so refresh keeps All / Campaigns / Mint spaces / Products.
   * All is the default; embedded in a campaign window, Campaigns is (it shows the highlight).
   */
  const discoverView = useMemo(() => {
    const d = searchParams.get("discover");
    if (d === "campaigns") return "campaigns";
    if (d === "mint-spaces" || d === "mintSpaces") return "mintSpaces";
    if (d === "products" || d === "apps") return "products";
    return highlightedCampaignId ? "campaigns" : "all";
  }, [searchParams, highlightedCampaignId]);

  const discoverCtaSubtitle = useMemo(() => {
    if (discoverView === "products") return t("home.discoverProductsCta");
    return t("home.discoverCampaignsCta");
  }, [discoverView, t]);

  const setDiscoverView = useCallback(
    (view) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (view === "mintSpaces") {
            next.set("discover", "mint-spaces");
          } else if (view === "products") {
            next.set("discover", "products");
          } else {
            if (view === "campaigns") next.set("discover", "campaigns");
            else next.delete("discover");
            next.delete("category");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [campaigns, setCampaigns] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subFilter, setSubFilter] = useState("all"); // "all", "ugc", or "clipping"
  const [spaceFilter, setSpaceFilter] = useState("all"); // "all" or "joined"
  const joinedOnly = isAuthenticated && discoverView === "mintSpaces" && spaceFilter === "joined";
  const categoryFilter = useMemo(() => {
    const c = searchParams.get("category");
    if (!c || c === "all") return "all";
    return c;
  }, [searchParams]);

  const setCategoryFilter = useCallback(
    (category) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!category || category === "all") next.delete("category");
          else next.set("category", category);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [showMyCampaigns, setShowMyCampaigns] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("q") || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    () => searchParams.get("q") || ""
  );
  const [mintSpaces, setMintSpaces] = useState([]);
  const [mintSpacesLoading, setMintSpacesLoading] = useState(false);

  const appCatalog = useMemo(() => getMintSpaceAppStoreCatalog(t), [t]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Persist search in `?q=` (with discover, etc.) — only writes; avoids fighting the input while typing
  useEffect(() => {
    const q = debouncedSearchQuery.trim();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const cur = next.get("q") || "";
        if (cur === q) return prev;
        if (q) next.set("q", q);
        else next.delete("q");
        return next;
      },
      { replace: true }
    );
  }, [debouncedSearchQuery, setSearchParams]);

  // A search started elsewhere (the top bar) lands here as a new `?q=`.
  // Only a change of the URL itself counts; typing writes `?q=` after the debounce.
  const urlQuery = searchParams.get("q") || "";
  useEffect(() => {
    if (urlQuery !== debouncedSearchQuery.trim()) {
      setSearchQuery(urlQuery);
      setDebouncedSearchQuery(urlQuery);
    }
  }, [urlQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // The Featured row: admins' picks, loaded once.
  useEffect(() => {
    let cancelled = false;
    getFeaturedCampaigns()
      .then((res) => {
        if (!cancelled && res?.success) setFeatured(res.data || []);
      })
      .catch(() => {
        /* the row simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (discoverView === "mintSpaces" || discoverView === "products") {
        return;
      }
      // The All view always shows every campaign; the filters belong to the Campaigns tab.
      const mine = discoverView === "campaigns" && showMyCampaigns;
      const sub = discoverView === "campaigns" ? subFilter : "all";
      // Only fetch "My Campaigns" if authenticated
      if (mine && !isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let response;

        if (mine && isAuthenticated) {
          // Fetch user's campaigns and filter by participant type
          const allMyCampaigns = await getMyCampaigns(debouncedSearchQuery);
          if (allMyCampaigns.success) {
            const participantType = audience === "creators" ? "creators" : "clippers";
            // Filter campaigns by participant type
            const filtered = allMyCampaigns.data.filter(campaign =>
              campaign.participantType === participantType || campaign.participantType === "both"
            );
            setCampaigns(filtered || []);
          }
        } else {
          // Fetch campaigns based on subFilter (works for both authenticated and non-authenticated users)
          const filters = { search: debouncedSearchQuery };
          if (sub === "ugc") {
            filters.participantType = "creators";
          } else if (sub === "clipping") {
            filters.participantType = "clippers";
          }

          response = await getAllCampaigns(filters);

          if (response && response.success) {
            // Additional client-side filtering to ensure accuracy
            let filteredCampaigns = response.data || [];
            if (sub !== "all") {
              const targetType = sub === "ugc" ? "creators" : "clippers";
              filteredCampaigns = filteredCampaigns.filter(campaign =>
                campaign.participantType === targetType || campaign.participantType === "both"
              );
            }
            setCampaigns(filteredCampaigns);
          }
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        if (setToast) {
          setToast(t("campaigns.failedToLoadCampaigns"));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [
    isAuthenticated,
    audience,
    showMyCampaigns,
    subFilter,
    debouncedSearchQuery,
    setToast,
    t,
    discoverView,
  ]);

  useEffect(() => {
    // Mint spaces, Products and All need discover mint-space payloads.
    if (discoverView === "campaigns") {
      return;
    }
    const run = async () => {
      try {
        setMintSpacesLoading(true);
        const res = await getDiscoverMintSpaces(debouncedSearchQuery, categoryFilter, { joined: joinedOnly });
        if (res.success) {
          setMintSpaces(res.data || []);
        } else {
          setMintSpaces([]);
        }
      } catch (e) {
        console.error("Error fetching mint spaces:", e);
        if (setToast) {
          setToast(t("home.failedToLoadMintSpaces") || "Could not load mint spaces.");
        }
        setMintSpaces([]);
      } finally {
        setMintSpacesLoading(false);
      }
    };
    run();
  }, [discoverView, debouncedSearchQuery, categoryFilter, joinedOnly, setToast, t]);

  const filteredMintSpaces = useMemo(() => {
    if (categoryFilter === "all") return mintSpaces;
    if (categoryFilter.includes(":")) {
      return mintSpaces.filter(
        (ms) => ms.category?.toLowerCase() === categoryFilter.toLowerCase()
      );
    }
    return mintSpaces.filter((ms) => {
      const { typeId } = parseCategory(ms.category);
      return typeId === categoryFilter || ms.category?.startsWith(`${categoryFilter}:`);
    });
  }, [mintSpaces, categoryFilter]);

  const productEntries = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();
    // Most joined this week first; the rest stay in the order their spaces came.
    const weeklyRank = ({ p }) => p.weeklyRank ?? Number.MAX_SAFE_INTEGER;
    return filteredMintSpaces
      .flatMap((ms) =>
        (ms.appPricing || [])
          .filter((p) => {
            if (!q) return true;
            const hay = `${p.name || ""} ${p.headline || ""} ${ms.name || ""} ${
              ms.owner?.businessName || ms.owner?.username || ""
            }`.toLowerCase();
            return hay.includes(q);
          })
          .map((p) => ({ ms, p }))
      )
      .sort((a, b) => weeklyRank(a) - weeklyRank(b));
  }, [filteredMintSpaces, debouncedSearchQuery]);

  const openCampaign = (campaign) => navigate(getCampaignListPath(campaign));
  const openSpace = useOpenSpace();
  const showFeatured =
    (discoverView === "all" || (discoverView === "campaigns" && !showMyCampaigns)) &&
    !debouncedSearchQuery.trim() &&
    featured.length > 0;
  // All is browsed through its sections; its search row shows only for a search already running.
  const showSearchRow = discoverView !== "all" || Boolean(searchQuery.trim());
  const userId = user?._id || user?.id;
  const isMySpace = (space) =>
    (space.owner?._id || space.owner) === userId || (space.members || []).some((member) => (member?._id || member) === userId);

  const searchPlaceholder =
    discoverView === "all"
      ? t("home.searchAll")
      : discoverView === "mintSpaces"
      ? t("home.searchMintSpaces")
      : discoverView === "products"
      ? t("home.searchProducts") || "Search products..."
      : t("home.searchCampaigns");

  const loadingState = <EmptyState title={t("common.loading")} />;
  const pageTitle = <h1 className="text-caps-screen-title uppercase text-label-primary">{t("home.discoverTitle")}</h1>;

  let results;
  if (discoverView === "all") {
    const loadingAll = loading || mintSpacesLoading;
    const someCampaigns = campaigns.slice(0, 3);
    const someProducts = productEntries.slice(0, 4);
    const someSpaces = filteredMintSpaces.slice(0, 4);
    results =
      loadingAll && !someCampaigns.length && !someSpaces.length ? (
        loadingState
      ) : !someCampaigns.length && !someProducts.length && !someSpaces.length ? (
        <EmptyState title={t("shell.noResults")} />
      ) : (
        <div className="flex flex-col gap-space-24">
          {someCampaigns.length > 0 && (
            <Section title={t("home.browseCampaigns")} viewAllLabel={t("common.seeAll")} onViewAll={() => setDiscoverView("campaigns")}>
              <div className={ALL_CAMPAIGN_GRID}>
                {someCampaigns.map((campaign) => (
                  <CampaignTile key={campaign._id} campaign={campaign} onOpen={openCampaign} language={language} t={t} />
                ))}
              </div>
            </Section>
          )}
          {someProducts.length > 0 && (
            <Section title={t("home.browseProducts") || "Products"} viewAllLabel={t("common.seeAll")} onViewAll={() => setDiscoverView("products")}>
              <div className={ALL_PRODUCT_GRID}>
                {someProducts.map(({ ms, p }) => (
                  <ProductTile
                    key={p._id}
                    space={ms}
                    product={p}
                    categoryLabel={ms.category ? getBusinessCategoryShortLabel(t, ms.category) : ""}
                    apps={managesMintSpace(ms, user?._id || user?.id) ? productFolderApps(ms, p, appCatalog) : null}
                    onOpen={navigate}
                    language={language}
                    t={t}
                  />
                ))}
              </div>
            </Section>
          )}
          {someSpaces.length > 0 && (
            <Section title={t("home.spacesShort")} viewAllLabel={t("common.seeAll")} onViewAll={() => setDiscoverView("mintSpaces")}>
              <div className={ALL_SPACE_GRID}>
                {someSpaces.map((ms) => (
                  <SpaceTile
                    key={ms._id}
                    space={ms}
                    categoryLabel={ms.category ? getBusinessCategoryShortLabel(t, ms.category) : ""}
                    isMine={isMySpace(ms)}
                    onOpen={openSpace}
                    language={language}
                    t={t}
                  />
                ))}
              </div>
            </Section>
          )}
        </div>
      );
  } else if (discoverView === "products") {
    results = mintSpacesLoading ? (
      loadingState
    ) : productEntries.length === 0 ? (
      <EmptyState
        title={t("home.noProductsFound") || "No products found"}
        text={
          searchQuery
            ? t("home.noProductsMatchSearch") || "No products match your search."
            : categoryFilter !== "all"
            ? t("home.noProductsInCategory") || "No products in this category."
            : t("home.noProductsAvailable") || "No products are available yet."
        }
      />
    ) : (
      <Stagger className={PRODUCT_GRID} stagger={0.07} animateOnMount>
        {productEntries.map(({ ms, p }) => (
          <StaggerItem key={p._id} className="flex h-full min-h-0">
            <ProductTile
              space={ms}
              product={p}
              categoryLabel={ms.category ? getBusinessCategoryShortLabel(t, ms.category) : ""}
              apps={managesMintSpace(ms, user?._id || user?.id) ? productFolderApps(ms, p, appCatalog) : null}
              onOpen={navigate}
              language={language}
              t={t}
            />
          </StaggerItem>
        ))}
      </Stagger>
    );
  } else if (discoverView === "mintSpaces") {
    results = mintSpacesLoading ? (
      loadingState
    ) : filteredMintSpaces.length === 0 ? (
      <EmptyState
        title={t("home.noMintSpacesFound")}
        text={
          searchQuery
            ? t("home.noMintSpacesMatchSearch")
            : categoryFilter !== "all"
            ? t("home.noMintSpacesInCategory")
            : joinedOnly
            ? t("joinedMints.empty")
            : t("home.noMintSpacesAvailable")
        }
      />
    ) : (
      <Stagger className={SPACE_GRID} stagger={0.06} animateOnMount>
        {filteredMintSpaces.map((ms) => (
          <StaggerItem key={ms._id} className="flex h-full min-h-0">
            <SpaceTile
              space={ms}
              categoryLabel={ms.category ? getBusinessCategoryShortLabel(t, ms.category) : ""}
              isMine={isMySpace(ms)}
              onOpen={openSpace}
              language={language}
              t={t}
            />
          </StaggerItem>
        ))}
      </Stagger>
    );
  } else if (loading) {
    results = loadingState;
  } else if (showMyCampaigns && !isAuthenticated) {
    results = (
      <div className="flex flex-col items-center gap-space-12 rounded-radius-xl border border-separator-default bg-bg-raised px-space-24 py-space-32 text-center">
        <p className="text-headline text-label-primary">Sign in to view your campaigns</p>
        <Button size="m" onClick={() => openAuthModal({ form: "signin" })}>
          Sign In
        </Button>
      </div>
    );
  } else if (campaigns.length === 0) {
    results = (
      <div className="flex flex-col items-center gap-space-12 rounded-radius-xl border border-separator-default bg-bg-raised px-space-24 py-space-32 text-center">
        <p className="text-headline text-label-primary">{t("home.noCampaignsFound")}</p>
        <p className="text-footnote text-label-secondary">
          {searchQuery
            ? t("home.noCampaignsMatchSearch") || "No campaigns match your search."
            : showMyCampaigns
            ? t("home.noCampaignsCreated") || "You haven't created any campaigns yet."
            : t("home.noCampaignsAvailable") || "There are no campaigns available at the moment."}
        </p>
        {showMyCampaigns && !searchQuery && (
          <Button size="m" onClick={() => navigate("/create-campaign")}>
            {t("campaigns.createYourFirstCampaign")}
          </Button>
        )}
      </div>
    );
  } else {
    results = (
      <Stagger className={CAMPAIGN_GRID} stagger={0.06} animateOnMount>
        {campaigns.map((campaign) => (
          <StaggerItem key={campaign._id} className="flex h-full min-h-0">
            <CampaignTile
              campaign={campaign}
              onOpen={openCampaign}
              isSelected={highlightId === campaign._id}
              showJoin={!showMyCampaigns}
              joinLabel={t("home.joinCampaignShort")}
              language={language}
              t={t}
            />
          </StaggerItem>
        ))}
      </Stagger>
    );
  }

  return (
    <div
      data-theme={isDark ? "dark" : "light"}
      className="home-feed flex w-full min-w-0 flex-col gap-space-24 font-ds text-label-primary"
    >
      {discoverView === "all" ? (
        // Figma's All frame has no page header: on desktop the title shares the row of the top actions.
        titleSlot ? createPortal(pageTitle, titleSlot) : pageTitle
      ) : discoverView === "mintSpaces" ? (
        <SpacesHero onCreate={() => navigate("/create-business")} t={t} />
      ) : (
        <header className="flex items-center gap-space-16 rounded-radius-xl border border-separator-default bg-bg-raised px-space-24 py-space-20">
          <div className="flex min-w-0 flex-1 flex-col gap-space-4">
            <p className="text-caps-label uppercase text-label-secondary">{t("home.discoverTitle")}</p>
            <h1 className="text-title-2 text-label-primary">{discoverCtaSubtitle}</h1>
          </div>
        </header>
      )}

      <div
        className={`flex flex-col gap-space-12 ${
          discoverView === "mintSpaces"
            ? "min-[1400px]:flex-row min-[1400px]:items-center min-[1400px]:justify-between"
            : "min-[900px]:flex-row min-[900px]:items-center min-[900px]:justify-between"
        }`}
      >
        <ViewTabs
          label={t("home.discoverTitle")}
          value={discoverView}
          onChange={setDiscoverView}
          className="min-[900px]:max-w-[600px]"
          options={[
            { value: "all", label: t("common.all") || "All" },
            { value: "campaigns", label: t("home.browseCampaigns") },
            { value: "mintSpaces", label: t("home.spacesShort") },
            { value: "products", label: t("home.browseProducts") || "Products" },
          ]}
        />
        {discoverView === "campaigns" ? (
          <SegmentedControl
            track="material"
            label={t("home.browseCampaigns")}
            value={subFilter}
            onChange={setSubFilter}
            className="min-[900px]:max-w-[420px]"
            options={[
              { value: "all", label: t("common.all") || "All" },
              { value: "ugc", label: t("home.ugc") },
              { value: "clipping", label: t("home.clipping") },
            ]}
          />
        ) : discoverView === "mintSpaces" ? (
          <div className="flex flex-col gap-space-12 min-[600px]:flex-row min-[600px]:items-center">
            <div className="self-start min-[600px]:self-auto">
              <DiscoverCategoryDropdown value={categoryFilter} onChange={setCategoryFilter} isDark={isDark} t={t} />
            </div>
            {isAuthenticated && (
              <SegmentedControl
                track="material"
                label={t("home.spacesShort")}
                value={spaceFilter}
                onChange={setSpaceFilter}
                className="min-[600px]:w-[340px]"
                options={[
                  { value: "all", label: t("common.all") || "All" },
                  { value: "joined", label: t("home.joinedSpaces") },
                ]}
              />
            )}
          </div>
        ) : discoverView === "products" ? (
          <DiscoverCategoryDropdown value={categoryFilter} onChange={setCategoryFilter} isDark={isDark} t={t} />
        ) : null}
      </div>

      {showFeatured && <FeaturedCampaigns campaigns={featured} onOpen={openCampaign} language={language} t={t} />}

      {showSearchRow && (
        <div className="flex flex-col gap-space-12 min-[600px]:flex-row min-[600px]:items-center min-[600px]:gap-space-24">
          <SearchField
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="min-[600px]:flex-1"
          />
          {isAuthenticated && discoverView === "campaigns" && (
            <Button
              size="m"
              variant={showMyCampaigns ? "filled" : "tinted"}
              aria-pressed={showMyCampaigns}
              onClick={() => setShowMyCampaigns(!showMyCampaigns)}
              className="self-start min-[600px]:self-auto"
            >
              {t("home.myCampaigns") || "My Campaigns"}
            </Button>
          )}
        </div>
      )}

      {results}
    </div>
  );
};

export default HomePage;
